import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { formatPayModDate } from "../../../../services/planChange";
import {
  HiCalendar,
  HiCheck,
  HiCreditCard,
  HiExclamation,
  HiLink,
  HiLocationMarker,
  HiReply,
} from "react-icons/hi";
import SideDrawer from "../../../atoms/SideDrawer";
import NoTranslate from "../../../atoms/NoTranslate";
import StatusBadge from "../../../atoms/StatusBadge";
import ActionButton from "../../../atoms/ActionButton";
import PaymentStepActions from "./PaymentStepActions";
import {
  CancelSubscriptionInfo,
  InvoiceLinkVariant,
  PaymentHistoryGroup,
  buildPlanEntries,
  formatAmount,
  isRecurringPlan,
  planKindOf,
  resolveCurrentPeriod,
  summarizePlans,
} from "./paymentHistoryUtils";
import {
  KIND_BADGE,
  STATE_BADGE,
  getNextDateLabel,
  getPlanTypeLabel,
  getRecurrencySuffix,
  getRefundReasonLabel,
  getStatusDisplay,
  periodSentence,
  planTitle,
} from "./paymentHistoryDisplay";

interface PlanTimelineDrawerProps {
  open: boolean;
  onClose: () => void;
  group: PaymentHistoryGroup | null;
  canCreatePaymentLink: boolean;
  invoiceLinks: Record<string, string | null>;
  onFetchInvoiceLink: (
    invoiceId: string,
    variant: InvoiceLinkVariant
  ) => Promise<string | null>;
  onOpenRefund: (
    paymentStepIdentification: string,
    amount: number,
    currency: string,
    description: string
  ) => void;
  onCancelSubscription?: (info: CancelSubscriptionInfo) => void;
  onPaymentRequestClick?: (paymentRequestLink: string) => void;
  existingPaymentLinks?: string[];
  onCopyCardUpdateLink?: () => void;
  isCopyingCardUpdateLink?: boolean;
  canCopyCardUpdateLink?: boolean;
  cardLinkJustCopied?: boolean;
}

const PlanTimelineDrawer: React.FC<PlanTimelineDrawerProps> = ({
  open,
  onClose,
  group,
  canCreatePaymentLink,
  invoiceLinks,
  onFetchInvoiceLink,
  onOpenRefund,
  onCancelSubscription,
  onPaymentRequestClick,
  existingPaymentLinks,
  onCopyCardUpdateLink,
  isCopyingCardUpdateLink = false,
  canCopyCardUpdateLink = false,
  cardLinkJustCopied = false,
}) => {
  const { t } = useTranslation();

  const entries = useMemo(
    () => (group ? buildPlanEntries(group) : []),
    [group]
  );
  const plan = useMemo(
    () => (group ? summarizePlans([group])[0] : undefined),
    [group]
  );
  const period = useMemo(
    () => (group ? resolveCurrentPeriod(group) : null),
    [group]
  );

  const isRecurring = isRecurringPlan(group?.payment_type);

  const isDegraded =
    plan?.state === "paymentFailed" || plan?.state === "suspended";
  const degradedDetail = periodSentence(period, t);

  const expected =
    period && period.end && period.days !== null
      ? {
          date: period.end,
          overdue: period.status === "lapsed",
          days: period.days,
        }
      : null;

  const expectedNode = expected && (
    <li className="relative">
      <span
        className={`absolute -left-[1.9rem] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white border-2 border-dashed bg-white ${
          expected.overdue
            ? "border-red-400 text-red-600"
            : "border-blue-400 text-blue-500"
        }`}
      >
        {expected.overdue ? (
          <HiExclamation size={12} />
        ) : (
          <HiCalendar size={12} />
        )}
      </span>

      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`text-sm font-bold ${
            expected.overdue ? "text-red-700" : "text-gray-500"
          }`}
        >
          <NoTranslate>
            {formatAmount(period!.amount, period!.currency)}
            {isRecurring && (
              <span className="text-xs font-medium">
                {" "}
                {getRecurrencySuffix(group?.payment_type || "", t)}
              </span>
            )}
          </NoTranslate>
        </span>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            expected.overdue
              ? "bg-red-100 text-red-700"
              : "bg-blue-50 text-blue-700"
          }`}
        >
          {expected.overdue
            ? t("paymentHistory.timeline.expectedOverdue")
            : t("paymentHistory.timeline.expectedNext")}
        </span>
      </div>

      <p className="text-xs text-gray-500 mt-0.5">
        {moment(expected.date).format("LL")}
        {" • "}
        {expected.days === 0
          ? t("paymentHistory.timeline.dueToday")
          : expected.overdue
          ? (t("paymentHistory.timeline.agoDays", {
              count: expected.days,
            }) as string)
          : (t("paymentHistory.timeline.inDays", {
              count: expected.days,
            }) as string)}
      </p>
    </li>
  );

  const noUpcomingNode = period &&
    !expected &&
    (period.status === "none" || period.status === "unknown") && (
      <li className="relative">
        <span className="absolute -left-[1.9rem] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white border-2 border-dashed border-gray-300 bg-white text-gray-400">
          <HiCalendar size={12} />
        </span>
        <p className="text-xs text-gray-500">{periodSentence(period, t)}</p>
      </li>
    );

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={group ? planTitle(group.planDescription, t) : ""}
      icon={HiCreditCard}
    >
      {!group ? null : (
        <div className="flex flex-col gap-4 pb-8">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {group.payment_type && (
              <StatusBadge
                color={KIND_BADGE[planKindOf(group.payment_type)].tone}
              >
                {getPlanTypeLabel(group.payment_type, t)}
              </StatusBadge>
            )}
            {plan?.state && (
              <StatusBadge color={STATE_BADGE[plan.state].tone}>
                {t(`paymentHistory.summary.state.${plan.state}`)}
              </StatusBadge>
            )}
            <span className="font-mono text-gray-500">
              <NoTranslate>{group.planCode}</NoTranslate>
            </span>
          </div>

          {isDegraded && plan && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="flex items-start gap-2">
                <HiExclamation
                  size={16}
                  className="mt-0.5 flex-shrink-0 text-red-600"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-red-700">
                    {
                      t("paymentHistory.summary.failedSince", {
                        date: formatPayModDate(plan.latestStepDate, "ll"),
                      }) as string
                    }
                  </p>
                  {degradedDetail && (
                    <p className="mt-0.5 text-xs text-gray-600">
                      {degradedDetail}
                    </p>
                  )}
                  {plan.state === "paymentFailed" &&
                    onCopyCardUpdateLink &&
                    canCopyCardUpdateLink && (
                      <div className="mt-2">
                        <ActionButton
                          onClick={onCopyCardUpdateLink}
                          isLoading={isCopyingCardUpdateLink}
                          colorScheme={cardLinkJustCopied ? "green" : "red"}
                          title={
                            <span className="inline-flex items-center gap-1.5">
                              {cardLinkJustCopied ? (
                                <HiCheck size={14} />
                              ) : (
                                <HiCreditCard size={14} />
                              )}
                              <span>
                                {cardLinkJustCopied
                                  ? t("common.copied")
                                  : t(
                                      "paymentModule.userPaymentHistory.copyCardLink"
                                    )}
                              </span>
                            </span>
                          }
                          aria-label={
                            t(
                              "paymentModule.userPaymentHistory.copyCardLinkHint"
                            ) as string
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            {t("paymentHistory.timeline.title", { count: entries.length })}
          </h4>

          <ol className="relative flex flex-col gap-5 border-l border-gray-200 ml-3 pl-5">
            {expected && !expected.overdue && expectedNode}
            {noUpcomingNode}

            <li className="relative">
              <span className="absolute -left-[1.9rem] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white bg-gray-900 text-white">
                <HiLocationMarker size={13} />
              </span>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-bold text-gray-900">
                  {t("paymentHistory.timeline.today")}
                </span>
                <span className="text-xs text-gray-500">
                  {moment().format("LL")}
                </span>
              </div>
            </li>

            {expected && expected.overdue && expectedNode}

            {entries.map((entry) => {
              const status = getStatusDisplay(
                entry.payment.paymentStatusEnum,
                t
              );
              const isRefund = entry.payment.paymentStatusEnum === "REFUNDED";

              return (
                <li
                  key={entry.payment.paymentStepIdentification}
                  className="relative"
                >
                  <span
                    className={`absolute -left-[1.9rem] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white ${status.bgColor} ${status.color}`}
                  >
                    <span className="scale-[0.6]">{status.icon}</span>
                  </span>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-gray-900">
                      <NoTranslate>
                        {isRefund
                          ? formatAmount(
                              -(
                                entry.paymentData.amountRefunded ||
                                entry.paymentData.amount
                              ),
                              entry.paymentData.currency
                            )
                          : formatAmount(
                              entry.paymentData.amount,
                              entry.paymentData.currency
                            )}
                        {isRecurring && !isRefund && (
                          <span className="text-xs font-medium text-gray-500">
                            {" "}
                            {getRecurrencySuffix(group.payment_type || "", t)}
                          </span>
                        )}
                      </NoTranslate>
                    </span>
                    <StatusBadge color={status.tone}>
                      {status.label}
                    </StatusBadge>
                    {entry.payment.paymentRequestUniqueLink &&
                      (onPaymentRequestClick &&
                      canCreatePaymentLink &&
                      existingPaymentLinks?.includes(
                        entry.payment.paymentRequestUniqueLink
                      ) ? (
                        <button
                          onClick={() =>
                            onPaymentRequestClick(
                              entry.payment.paymentRequestUniqueLink!
                            )
                          }
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                          title={t("paymentHistory.viewPaymentLink") as string}
                          aria-label={`View payment link ${entry.payment.paymentRequestUniqueLink}`}
                        >
                          <HiLink size={12} />
                          <NoTranslate>
                            {entry.payment.paymentRequestUniqueLink}
                          </NoTranslate>
                        </button>
                      ) : (
                        <span className="min-w-0 break-all text-xs text-gray-500">
                          <NoTranslate>
                            {entry.payment.paymentRequestUniqueLink}
                          </NoTranslate>
                        </span>
                      ))}
                  </div>

                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatPayModDate(entry.payment.paymentDateTime, "lll")}
                  </p>

                  {entry.payment.paymentNextDate &&
                    !isRefund &&
                    isRecurring && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {getNextDateLabel(entry.payment.paymentStatusEnum, t)}:{" "}
                        {moment(entry.payment.paymentNextDate).format("ll")}
                      </p>
                    )}

                  {entry.refunds.map((refund) => (
                    <div
                      key={refund.payment.paymentStepIdentification}
                      className="mt-2 flex flex-wrap items-center gap-2 text-xs"
                    >
                      <HiReply
                        size={14}
                        className="text-purple-600 flex-shrink-0"
                      />
                      <span className="font-bold text-purple-700">
                        <NoTranslate>
                          {formatAmount(
                            -(
                              refund.paymentData.amountRefunded ||
                              refund.paymentData.amount
                            ),
                            refund.paymentData.currency
                          )}
                        </NoTranslate>
                      </span>
                      <span className="text-gray-500">
                        {t("paymentHistory.refundedOn")}{" "}
                        {formatPayModDate(refund.payment.paymentDateTime, "ll")}
                      </span>
                      {refund.paymentData.refundReason && (
                        <span className="text-gray-700 font-medium">
                          {getRefundReasonLabel(
                            refund.paymentData.refundReason,
                            t
                          )}
                        </span>
                      )}
                    </div>
                  ))}

                  {entry.refundedTotal > 0 && (
                    <p className="mt-1 text-xs">
                      <span className="text-gray-500">
                        {t("paymentHistory.netAmount")}:{" "}
                      </span>
                      <span className="font-bold text-gray-900">
                        <NoTranslate>
                          {formatAmount(
                            entry.paymentData.amount - entry.refundedTotal,
                            entry.paymentData.currency
                          )}
                        </NoTranslate>
                      </span>
                    </p>
                  )}

                  <PaymentStepActions
                    entry={entry}
                    planDescription={planTitle(group.planDescription, t)}
                    planPaymentType={group.payment_type || ""}
                    canCreatePaymentLink={canCreatePaymentLink}
                    invoiceLinks={invoiceLinks}
                    onFetchInvoiceLink={onFetchInvoiceLink}
                    onOpenRefund={onOpenRefund}
                    onCancelSubscription={onCancelSubscription}
                    compact
                  />
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </SideDrawer>
  );
};

export default PlanTimelineDrawer;
